import { useCallback } from 'react';
import { HighnoteTransactionEvent } from 'API';
import { useAppSelector } from 'app/store';
import { selectCurrentPaidolUser } from 'app/store/userCompaniesSlice';
import { levelThatClears, StatusKey } from 'util/workflowStatus';

/**
 * Unlike every other capability in useUserRole this is per transaction, not per
 * role: a group admin clears a step only for the group that owns that row, and
 * only at the level they were assigned. Admins are handled by their own
 * capability at the call site, mirroring the skip they get on the write path.
 *
 * A transaction is owned by every group its steps came from, of either kind, and
 * holding the level in any one of them is enough — the same union the write path
 * authorizes against. The card's group comes from the card the resolver attaches
 * to each row, whose cardGroupId is the card's group NOW, never the frozen
 * snapshot on the row; project groups own by coding, so the resolver names them
 * outright rather than making this re-derive which projects a group lists.
 */
export const useCanApproveGroupStep = () => {
  const currentPaidolUser = useAppSelector(selectCurrentPaidolUser);

  return useCallback(
    (transaction: HighnoteTransactionEvent | null | undefined, status: StatusKey | null): boolean => {
      const required = levelThatClears(status, transaction?.groupApprovalLevels ?? 0);
      if (required == null) return false;

      const owningGroupId = transaction?.card?.cardGroupId;
      if (
        owningGroupId &&
        owningGroupId !== 'NO_GROUP' &&
        (currentPaidolUser?.managedGroups?.items ?? []).some(
          (item) => item?.cardGroupId === owningGroupId && (item?.approvalLevels ?? []).includes(required)
        )
      ) {
        return true;
      }

      const owningProjectGroupIds = transaction?.owningProjectGroupIds ?? [];
      if (owningProjectGroupIds.length === 0) return false;

      return (currentPaidolUser?.managedProjectsGroups?.items ?? []).some(
        (item) =>
          item?.projectGroupId &&
          owningProjectGroupIds.includes(item.projectGroupId) &&
          (item?.approvalLevels ?? []).includes(required)
      );
    },
    [currentPaidolUser]
  );
};
